import { Button } from '@/components/ui/button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import axiosClient from '@/lib/axiosClient';
import { fetcher } from '@/lib/queryClient';
import { useAuthDialog } from '@/context/auth-dialog.context';
import { cn } from '@/lib/utils';

interface GameRatingProps {
  gameId: number;
}

const Star = ({
  filled,
  number,
  onClick,
  disabled,
}: {
  filled: boolean;
  number: number;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    className={`relative md:w-24 md:h-24 w-16 h-16 ${
      filled ? 'text-yellow-400' : 'text-purple-400'
    }`}
    onClick={onClick}
    disabled={disabled}
    data-goal-id={`rate_game_clicked`}
  >
    <svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('w-full h-full', filled ? 'fill-primary' : 'fill-purple-500/30')}
    >
      <path d="M35.7652 17.04C42.1002 5.68 45.2652 0 50.0002 0C54.7352 0 57.9002 5.68 64.2352 17.04L65.8752 19.98C67.6752 23.21 68.5752 24.825 69.9752 25.89C71.3752 26.955 73.1252 27.35 76.6252 28.14L79.8052 28.86C92.1052 31.645 98.2502 33.035 99.7152 37.74C101.175 42.44 96.9852 47.345 88.6002 57.15L86.4302 59.685C84.0502 62.47 82.8552 63.865 82.3202 65.585C81.7852 67.31 81.9652 69.17 82.3252 72.885L82.6552 76.27C83.9202 89.355 84.5552 95.895 80.7252 98.8C76.8952 101.705 71.1352 99.055 59.6252 93.755L56.6402 92.385C53.3702 90.875 51.7352 90.125 50.0002 90.125C48.2652 90.125 46.6302 90.875 43.3602 92.385L40.3802 93.755C28.8652 99.055 23.1052 101.705 19.2802 98.805C15.4452 95.895 16.0802 89.355 17.3452 76.27L17.6752 72.89C18.0352 69.17 18.2152 67.31 17.6752 65.59C17.1452 63.865 15.9502 62.47 13.5702 59.69L11.4002 57.15C3.0152 47.35 -1.1748 42.445 0.285198 37.74C1.7452 33.035 7.9002 31.64 20.2002 28.86L23.3802 28.14C26.8752 27.35 28.6202 26.955 30.0252 25.89C31.4302 24.825 32.3252 23.21 34.1252 19.98L35.7652 17.04Z" />
    </svg>

    <span
      className={`absolute inset-0 flex items-center justify-center text-xl sm:text-4xl font-bold mt-2 ${
        filled ? 'text-yellow-900' : 'text-white'
      }`}
    >
      {number}
    </span>
  </button>
);

export function GameRating({ gameId }: GameRatingProps) {
  const { user } = useAuth();
  const { openDialog } = useAuthDialog();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: ratingData, isLoading: isLoadingRating } = useQuery<{
    rating: { rating: number } | null;
    canRate: boolean;
  }>({
    queryKey: [`/api/games/${gameId}/rating`],
    queryFn: fetcher,
    enabled: !!gameId && !!user, // Only fetch if logged in and gameId is valid
  });

  // Mutation for updating/creating rating - Moved inside
  const rateMutation = useMutation({
    mutationFn: async (newRating: number) => {
      await axiosClient.put(`/api/games/${gameId}/rating`, { rating: newRating });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/games/${gameId}/rating`] });
      toast({ title: 'Успех', description: 'Оценка сохранена' });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Ошибка',
        description: `Не удалось сохранить оценку: ${error.message}`,
      });
    },
  });

  // Mutation for deleting rating - Moved inside
  const deleteRatingMutation = useMutation({
    mutationFn: async () => {
      await axiosClient.delete(`/api/games/${gameId}/rating`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/games/${gameId}/rating`] });
      toast({ title: 'Успех', description: 'Оценка удалена' });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Ошибка',
        description: `Не удалось удалить оценку: ${error.message}`,
      });
    },
  });

  const handleRateClick = (starNumber: number) => {
    if (!user) {
      openDialog();
      return;
    }
    rateMutation.mutate(starNumber);
  };

  const currentRating = ratingData?.rating?.rating ?? null;
  const isDeleting = deleteRatingMutation.isPending;
  const isRating = rateMutation.isPending;

  return (
    <div className="bg-purple-300/[0.1] py-10 px-8 rounded-2xl h-min">
      <div className="flex justify-between items-center mb-2">
        <h3 className="!text-2xl sm:!text-4xl font-bold sm:font-extrabold">
          {currentRating ? `Ваша оценка – ${currentRating}` : 'Оцените игру'}
        </h3>
        {currentRating && (
          <Button
            variant="link"
            onClick={() => deleteRatingMutation.mutate()}
            disabled={isDeleting || isRating}
            className="text-muted-foreground p-0 h-auto"
            data-goal-id="delete_rating_clicked"
          >
            {isDeleting ? 'Удаление...' : 'Удалить'}
          </Button>
        )}
      </div>
      <p className="text-muted-foreground text-sm md:text-xl lg:text-2xl mb-4">
        Это позволит нам сделать игры еще круче
      </p>
      <div className="flex gap-2 justify-start mt-10">
        {[1, 2, 3, 4, 5].map((starNumber) => (
          <Star
            key={starNumber}
            filled={currentRating ? starNumber <= currentRating : false}
            number={starNumber}
            onClick={() => handleRateClick(starNumber)}
            disabled={isRating || isDeleting}
          />
        ))}
      </div>
    </div>
  );
}
